import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BsChevronLeft, BsShieldCheck, BsTrophy, BsPeople } from 'react-icons/bs';
import { useLeagueDetail } from '../../hooks/useLeagueDetail';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ErrorMessage } from '../../components/ui/ErrorMessage';
import { LeagueRanking } from './components/LeagueRanking';

export const LeagueDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { league, players, loading, error, refetch } = useLeagueDetail(id || '');

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No definida';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        {isActive ? 'Activa' : 'Inactiva'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner message="Cargando detalles de la liga..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error} onRetry={refetch} />
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h3 className="mt-2 text-sm font-medium text-gray-900">Liga no encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">
              La liga solicitada no existe o no tienes permisos para verla.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/leagues')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <BsChevronLeft className="mr-1 h-4 w-4" />
            Volver a ligas
          </button>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BsShieldCheck className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{league.name}</h1>
                  <div className="mt-1">
                    {getStatusBadge(league.is_active)}
                  </div>
                </div>
              </div>
              
              <div className="text-right text-sm text-gray-500">
                <div>ID: {league.id}</div>
              </div>
            </div>

            {league.description && (
              <div className="mt-4">
                <p className="text-gray-600">{league.description}</p>
              </div>
            )}

            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center">
                  <BsPeople className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Jugadores</p>
                    <p className="text-2xl font-semibold text-gray-900">{players.length}</p>
                  </div>
                </div>
              </div>

              {league.start_date && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <span className="text-lg mr-3">📅</span>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Fecha inicio</p>
                      <p className="text-sm font-semibold text-gray-900">{formatDate(league.start_date)}</p>
                    </div>
                  </div>
                </div>
              )}

              {league.end_date && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <span className="text-lg mr-3">🏁</span>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Fecha fin</p>
                      <p className="text-sm font-semibold text-gray-900">{formatDate(league.end_date)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Ranking */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <BsTrophy className="h-5 w-5 text-yellow-500" />
              <h2 className="text-lg font-semibold text-gray-900">
                Ranking de la Liga
              </h2>
            </div>
          </div>
          
          <div className="p-6">
            <LeagueRanking players={players} />
          </div>
        </div>
      </div>
    </div>
  );
};
